/**
 * Coupon Plugin
 * Handles discount code application and removal in cart and mini-cart using /cart/update.js and Storefront GraphQL API.
 */
class CouponPlugin {
  constructor() {
    this.use_error_msg = 'Enter a valid discount code or gift card';
    this.emtry_error_msg = 'Enter a discount code or gift card';
    this.storefrontAccessToken = '6c75c21c9c519bd263d817a5d122cf9a';
    this.initEvent();
  }

  // Register events
  initEvent() {
    // Event delegation for clicks
    document.addEventListener('click', (event) => {
      // Apply discount button
      if (event.target.id === 'apply-discount-btn' || event.target.closest('#apply-discount-btn')) {
        this.handleApplyDiscount(event);
      }

      // Remove discount button
      const removeBtn = event.target.closest('.remove-discount-btn');
      if (removeBtn) {
        this.handleRemoveDiscount(event);
      }
    });

    // Enter key support for input
    document.addEventListener('keypress', (event) => {
      if (event.key === 'Enter' && document.activeElement && document.activeElement.id === 'discount-code-input') {
        this.handleApplyDiscount(event);
      }
    });
  }

  // Handle discount code application
  async handleApplyDiscount(event) {
    event.preventDefault();

    const discountInput = document.getElementById('discount-code-input');
    if (!discountInput) return;

    const code = discountInput.value.trim();
    if (!code) {
      this.showMessage(this.emtry_error_msg, 'error');
      return;
    }

    const applyBtn = document.getElementById('apply-discount-btn');
    if (applyBtn) {
      applyBtn.disabled = true;
      applyBtn.classList.add('loading');
    }

    try {
      const result = await this.applyDiscountCode(code);

      if (applyBtn) {
        applyBtn.disabled = false;
        applyBtn.classList.remove('loading');
      }

      if (result.success) {
        discountInput.value = '';
        this.showMessage('', 'success');
        // Trigger cart change event
        document.dispatchEvent(
          new CustomEvent('on:cart:change', {
            bubbles: true,
            cancelable: false
          })
        );
      } else {
        document.dispatchEvent(
          new CustomEvent('on:cart:change', {
            bubbles: true,
            cancelable: false
          })
        );
        setTimeout(() => {
          this.showMessage(result.message || this.use_error_msg, 'error');
        }, 800);
      }
    } catch (error) {
      if (applyBtn) {
        applyBtn.disabled = false;
        applyBtn.classList.remove('loading');
      }

      this.showMessage(this.use_error_msg, 'error');
      console.error(error);
    }
  }

  // Handle discount code removal
  async handleRemoveDiscount(event) {
    event.preventDefault();

    const btn = event.target.closest('.remove-discount-btn');
    if (!btn) return;

    const codeToRemove = (btn.dataset.discountTitle || btn.getAttribute('data-discount-title') || '').trim();

    try {
      btn.disabled = true;
      btn.classList.add('loading');

      const result = await this.removeDiscountCode(codeToRemove);

      if (btn) {
        btn.disabled = false;
        btn.classList.remove('loading');
      }

      if (result.success) {
        this.showMessage('', 'success');

        // Trigger cart change event to refresh UI
        document.dispatchEvent(
          new CustomEvent('on:cart:change', {
            bubbles: true,
            cancelable: false
          })
        );
      } else {
        this.showMessage(result.message || this.use_error_msg, 'error');
      }
    } catch (error) {
      if (btn) {
        btn.disabled = false;
        btn.classList.remove('loading');
      }

      console.error(error);
    }
  }

  // Display status message
  showMessage(message, type) {
    const discountMessage = document.getElementById('discount-message');
    if (!discountMessage) return;

    discountMessage.textContent = message || '';
    discountMessage.className = 'discount-message';
    if (type && message) {
      discountMessage.classList.add(type);
    }
  }

  // Clear cookie helper
  clearDiscountCookies() {
    try {
      const hostname = window.location.hostname;
      const rootDomain = hostname.startsWith('www.') ? hostname.slice(4) : hostname;
      const domains = ['', hostname, `.${hostname}`, `.${rootDomain}`];
      const paths = ['/', '/cart', '/checkout'];
      domains.forEach((d) => {
        paths.forEach((p) => {
          document.cookie = `discount_code=; path=${p}; expires=Thu, 01 Jan 1970 00:00:00 GMT; ${d ? `domain=${d};` : ''}`;
        });
      });
    } catch (e) {
      console.error('Failed to clear discount cookies:', e);
    }
  }

  // Apply discount code via /cart/update.js & GraphQL
  async applyDiscountCode(code) {
    try {
      // 1. Post to /cart/update.js
      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          discount: code
        })
      });

      let updatedCart = await response.json();

      let hasDiscount = (updatedCart.cart_level_discount_applications &&
        updatedCart.cart_level_discount_applications.length > 0)
        || (updatedCart.discount_codes && updatedCart.discount_codes.some(d => d.applicable))
        || (updatedCart.total_discount && updatedCart.total_discount > 0)
        || (updatedCart.items && updatedCart.items.some(item => (item.line_level_discount_allocations && item.line_level_discount_allocations.length > 0) || (item.discounts && item.discounts.length > 0)));


      // If not active yet, also apply via Storefront GraphQL
      if (!hasDiscount && updatedCart.token) {
        const currentCodes = (updatedCart.discount_codes || [])
          .filter((d) => d.applicable)
          .map((d) => d.code);
        const newCodesList = Array.from(new Set([...currentCodes, code.trim()]));

        const gqlResult = await this.updateCartDiscountCodesViaGraphQL(updatedCart.token, newCodesList);
        const cartData = gqlResult?.data?.cartDiscountCodesUpdate?.cart;
        const userErrors = gqlResult?.data?.cartDiscountCodesUpdate?.userErrors || [];

        let applied = false;
        if (userErrors.length === 0 && cartData?.discountCodes) {
          applied = cartData.discountCodes.some(
            (d) => d.code.toLowerCase() === code.trim().toLowerCase() && d.applicable
          );
        }

        const cartResponse = await fetch(`/cart.js?t=${Date.now()}`);
        updatedCart = await cartResponse.json();

        if (applied) {
          return { success: true, cart: updatedCart, message: '' };
        } else {
          return { success: false, cart: updatedCart, message: this.use_error_msg };
        }
      }

      if (hasDiscount) {
        return { success: true, cart: updatedCart, message: '' };
      } else {
        return { success: false, cart: updatedCart, message: this.use_error_msg };
      }
    } catch (error) {
      console.error('Failed to apply discount code:', error);
      return { success: false, error: error.message, message: this.use_error_msg };
    }
  }

  // Remove discount code via /cart/update.js & GraphQL
  async removeDiscountCode(codeToRemove) {
    try {
      // 1. Clear via /cart/update.js
      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          discount: ''
        })
      });

      let updatedCart = await response.json();

      // 2. Clear target code from GraphQL cart discount codes
      if (updatedCart.token) {
        const currentCodes = (updatedCart.discount_codes || []).map((d) => d.code);
        const targetLower = (codeToRemove || '').toLowerCase();
        const remainingCodes = currentCodes.filter(
          (c) => c && c.toLowerCase() !== targetLower && c.toLowerCase() !== 'clear'
        );

        await this.updateCartDiscountCodesViaGraphQL(updatedCart.token, remainingCodes);
        const cartResponse = await fetch(`/cart.js?t=${Date.now()}`);
        updatedCart = await cartResponse.json();
      }

      // 3. Clear cookie
      this.clearDiscountCookies();

      return {
        success: true,
        cart: updatedCart,
        message: ''
      };
    } catch (error) {
      console.error('Failed to remove discount code:', error);
      return { success: false, error: error.message, message: this.use_error_msg };
    }
  }

  // Storefront GraphQL call
  async updateCartDiscountCodesViaGraphQL(cartToken, discountCodes) {
    const cartId = cartToken.startsWith('gid://') ? cartToken : `gid://shopify/Cart/${cartToken}`;
    const query = `
      mutation cartDiscountCodesUpdate($cartId: ID!, $discountCodes: [String!]) {
        cartDiscountCodesUpdate(cartId: $cartId, discountCodes: $discountCodes) {
          cart {
            id
            discountCodes {
              code
              applicable
            }
          }
          userErrors {
            field
            message
          }
        }
      }
    `;

    const response = await fetch('/api/2024-04/graphql.json', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': this.storefrontAccessToken,
      },
      body: JSON.stringify({
        query,
        variables: {
          cartId,
          discountCodes: discountCodes || []
        }
      })
    });

    return await response.json();
  }

  // Get cart data
  async getCartData() {
    try {
      const response = await fetch(`/cart.js?t=${Date.now()}`);
      return await response.json();
    } catch (error) {
      console.error('Failed to fetch cart data:', error);
      throw error;
    }
  }
}

window._couponPlugin = new CouponPlugin();